import { ObjectId, PickSampleExt, rotationFromDirection } from "@novorender/api";
import { mat3, quat, vec2, vec3, vec4 } from "gl-matrix";
import { MouseEventHandler, MutableRefObject, useRef } from "react";

import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { featuresConfig } from "config/features";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { readyPdfObjectIds } from "contexts/explorerGlobals/reducer";
import {
    HighlightCollection,
    highlightCollectionsActions,
    useDispatchHighlightCollections,
    useHighlightCollections,
} from "contexts/highlightCollections";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { lastPickSampleActions, useDispatchLastPickSample } from "contexts/lastPickSample";
import { PickingSnapType, usePickingSessionRef } from "contexts/pickingSession";
import { isPdfOrDrawingObjectId } from "dataAccess";
import { useGoToStation } from "features/alignment/hooks/useGoToStation";
import { alignmentActions } from "features/alignment/slice";
import { USER_SELECTED_ALIGNMENT_NAME } from "features/alignment/utils";
import { useArcgisCanvasClickHandler } from "features/arcgis/hooks/useArcgisCanvasHandler";
import { areaActions } from "features/area";
import { backfillPlaneBounds } from "features/clippingPlanes/planeBoundsBackfill";
import { coordinatesActions } from "features/coordinates/coordinatesSlice";
import { useAddCoordinate } from "features/coordinates/hooks/useAddCoordinate";
import { useLocationFormAssetClickHandler } from "features/forms/hooks/useLocationFormAssetClickHandler";
import { usePlaceLocationForm } from "features/forms/hooks/usePlaceLocationForm";
import { heightProfileActions } from "features/heightProfile";
import { jiraActions } from "features/jira";
import { manholeActions } from "features/manhole";
import { markersActions, selectAllCollections, selectSelectedMarker } from "features/markers";
import { isWithinMarkerCreationSuppressionWindow } from "features/markers/markerCreationSuppression";
import { MarkerCollection } from "features/markers/types";
import {
    measureActions,
    selectMeasure,
    selectMeasureHoverSettings,
    selectMeasurePickSettings,
    selectObjectMeasureIds,
} from "features/measure";
import { dismissObjectMeasure } from "features/measure/objectMeasureDismiss";
import { orthoCamActions, selectCrossSectionClipping, selectCrossSectionPoint } from "features/orthoCam";
import { useAddLaser } from "features/outlineLaser/hooks/useAddLaser";
import { clippingOutlineLaserActions } from "features/outlineLaser/outlineLaserSlice";
import { useAddPointToPointLine } from "features/pointLine/hooks/useAddPointToPointLine";
import { selectShowPropertiesStamp } from "features/properties/slice";
import { useSectionPlaneUndoRedo } from "features/sectionPlane/sectionPlaneUndoRedo";
import { undoRedoActions } from "features/undoRedo";
import { useAbortController } from "hooks/useAbortController";
import useFlyTo from "hooks/useFlyTo";
import { useIsMobileMeasureInteraction } from "hooks/useIsMobileMeasureInteraction";
import { useOpenWidget } from "hooks/useOpenWidget";
import { useSuccessiveClickCount } from "hooks/useSuccessiveClickCount";
import {
    selectIsProcoreDesign,
    selectNewDesign,
    selectNodeClassSelectionEnabled,
    selectSelectionResolution,
} from "slices/explorer";
import { telemetry } from "telemetry";
import { ExtendedMeasureEntity, NodeType, ViewMode } from "types/misc";
import { isAppBuild } from "utils/buildType";
import { appendInPlace, hasMouseSupport, isRealVec, measureObjectIsVertex } from "utils/misc";
import {
    extractObjectIds,
    getAncestorSearchDepth,
    getBigEnoughObjectBoundsW,
    getObjectBoundsW,
    getObjectMetadataRotation,
    isTerrainObjectId,
} from "utils/objectData";
import { searchByPatterns, searchDeepByPatterns } from "utils/search";
import { restoreUndoIds, snapshotUndoIds } from "utils/undoIds";
import { queryView } from "utils/viewQueries";

import { getSnapAdjacentVertices, getSnapInfo, PointSnapInfo } from "../measureSnap";
import { consumeMobileMenuDismissed } from "../mobileMenuDismissalFlag";
import { getPlaneCreationNormal, snapSectionPlaneNormalToAxis } from "../planeCreationNormal";
import {
    renderActions,
    selectCamera,
    selectCameraType,
    selectCanvasSelectionEnabled,
    selectClippingPlanes,
    selectCurrentPickerGeneratedParametricData,
    selectIsXRayActive,
    selectMainObject,
    selectMainObjects,
    selectPickCameraPlane,
    selectPicker,
    selectSecondaryHighlightProperty,
    selectSectionPlaneLimitReached,
    selectSelectMultiple,
    selectTerrain,
    selectViewMode,
} from "../renderSlice";
import { resolveSelectionTarget } from "../selectionResolution";
import { CameraType, Picker, StampKind } from "../types";
import { applyCameraDistanceToMeasureTolerance, findCameraAlignedPlane, getLocalRotationAroundNormal } from "../utils";
import { abortPendingSelectionForBackgroundClick } from "./selectionAbort";
import {
    beginOptimisticPaintAttempt,
    discardPendingOptimisticPaint,
    getPendingOptimisticPaint,
    setPendingOptimisticPaint,
} from "./sharedOptimisticPaint";
import { invalidateSelectBoxAbort } from "./sharedSelectBoxAbort";
import { abortSharedSelection } from "./sharedSelectionAbort";
import { useCanvasContextMenuHandler } from "./useCanvasContextMenuHandler";

/**
 * Time window (in milliseconds) after a select box completion during which
 * background clicks are ignored to prevent immediately clearing the selection.
 */
const SELECT_BOX_COMPLETION_GUARD_MS = 200;

/** Two taps on one object closer together than this are a double-tap - about what iOS uses. */
const DOUBLE_TAP_MS = 300;

/**
 * Helper function to consistently deselect markers with proper callback invocation.
 * Centralizes the deselection logic to ensure all paths behave identically.
 */
function deselectMarkerWithCallback(
    selectedMarker: { collectionId: string; markerId: string } | null,
    markerCollections: Record<string, MarkerCollection>,
    dispatch: ReturnType<typeof useAppDispatch>,
): void {
    if (!selectedMarker) {
        return;
    }

    // Get config from collection to invoke onDeselect callback
    const collection = markerCollections[selectedMarker.collectionId];
    const config = collection?.config;
    if (config?.onDeselect) {
        // Get the marker data to pass to callback
        const marker = collection?.markers[selectedMarker.markerId];
        if (marker) {
            config.onDeselect(marker);
        }
    }

    // Dispatch Redux action to update state
    dispatch(markersActions.deselectMarker());
}

/**
 * Checks if we're within the guard window after a select box completion.
 * Used to prevent clicks/interactions from interfering with select box results.
 *
 * @param lastSelectBoxCompletionTime - Ref to the timestamp when select box last completed
 * @returns true if we're within the guard window, false otherwise
 */
function isWithinSelectBoxGuardWindow(lastSelectBoxCompletionTime: MutableRefObject<number> | undefined): boolean {
    if (!lastSelectBoxCompletionTime) {
        return false;
    }

    const timeSinceSelectBox = Date.now() - lastSelectBoxCompletionTime.current;
    return timeSinceSelectBox < SELECT_BOX_COMPLETION_GUARD_MS;
}

export function useCanvasClickHandler({
    pointerDownStateRef,
    lastSelectBoxCompletionTime,
}: {
    pointerDownStateRef: MutableRefObject<
        | {
              timestamp: number;
              x: number;
              y: number;
          }
        | undefined
    >;
    lastSelectBoxCompletionTime?: MutableRefObject<number>;
}) {
    const dispatch = useAppDispatch();
    const { addClippingPlaneWithUndo } = useSectionPlaneUndoRedo();
    const highlightedObjects = useHighlighted();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const highlightCollections = useHighlightCollections();
    const {
        state: { view, canvas, db, dataAccess, size, internalEventEmitter, pdfObjectDiscovery },
    } = useExplorerGlobals();

    const cameraType = useAppSelector(selectCameraType);
    const mainObject = useAppSelector(selectMainObject);
    const mainObjects = useAppSelector(selectMainObjects);
    const selectMultiple = useAppSelector(selectSelectMultiple);
    const cameraState = useAppSelector(selectCamera);
    const measure = useAppSelector(selectMeasure);
    const pickingSessionRef = usePickingSessionRef();
    const measureHoverSettings = useAppSelector(selectMeasureHoverSettings);
    const picker = useAppSelector(selectPicker);
    const pickCameraPlane = useAppSelector(selectPickCameraPlane);
    const measurePickSettings = useAppSelector(selectMeasurePickSettings);
    const crossSectionPoint = useAppSelector(selectCrossSectionPoint);
    const crossSectionClipping = useAppSelector(selectCrossSectionClipping);
    const viewMode = useAppSelector(selectViewMode);
    const showPropertiesStamp = useAppSelector(selectShowPropertiesStamp);
    const { planes } = useAppSelector(selectClippingPlanes);
    const sectionPlaneLimitReached = useAppSelector(selectSectionPlaneLimitReached);
    const allowGeneratedParametric = useAppSelector(selectCurrentPickerGeneratedParametricData);
    const newDesign = useAppSelector(selectNewDesign);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isMobileMeasureInteraction = useIsMobileMeasureInteraction();
    const terrain = useAppSelector(selectTerrain);
    const canvasSelectionEnabled = useAppSelector(selectCanvasSelectionEnabled);
    // X-ray is read off the store rather than selected: the click awaits the pick, the
    // PDF check and the resolve before it touches the selection, so a value captured on
    // render is stale by then and an isolation that turns X-ray on mid-flight would lose
    // to the landing pick.
    const store = useAppStore();
    const selectionResolution = useAppSelector(selectSelectionResolution);
    const nodeClassSelectionEnabled = useAppSelector(selectNodeClassSelectionEnabled);
    const selectedMarker = useAppSelector(selectSelectedMarker);
    const markerCollections = useAppSelector(selectAllCollections);
    const [dblClickZoomAbortControllerRef, abortDblClickZoom] = useAbortController();

    const [secondaryHighlightAbortController, abortSecondaryHighlight] = useAbortController();
    const currentSecondaryHighlightQuery = useRef("");
    const secondaryHighlightProperty = useAppSelector(selectSecondaryHighlightProperty);

    const arcgisCanvasClickHandler = useArcgisCanvasClickHandler();
    const placeLocationForm = usePlaceLocationForm();
    const locationFormAssetClickHandler = useLocationFormAssetClickHandler();
    const openWidget = useOpenWidget();
    const dispatchLastPickSample = useDispatchLastPickSample();
    const { showContextMenuStamp } = useCanvasContextMenuHandler();

    const addLaser = useAddLaser();
    const addCoordinate = useAddCoordinate();
    const addPointToPointLine = useAddPointToPointLine();

    const goToStation = useGoToStation();
    const flyTo = useFlyTo();
    const clickCountRef = useSuccessiveClickCount();
    const lastClickedObjectIdRef = useRef<ObjectId | null>(null);
    /** The last tap that acted in the object-to-object tool, to tell a double-tap on touch. */
    const lastObjectMeasureTapRef = useRef<{ objectId: ObjectId; timeStamp: number } | undefined>(undefined);
    // The scene's non-selectable PDF ids (Procore), once the discovery started at init has
    // finished. Complete at that point, so a click takes a synchronous early-exit on a hit
    // and treats a miss as "not a PDF". `undefined` until then, which puts every id through
    // an async PDF check before any optimistic highlight (see the Picker.Object branch below).
    const knownPdfObjectIds = isProcoreDesign ? readyPdfObjectIds(pdfObjectDiscovery) : undefined;

    const isEntitySelected = (entity: ExtendedMeasureEntity): boolean => {
        const currentEntities = measure.selectedEntities[measure.currentIndex];
        if (!currentEntities) {
            return false;
        }
        return currentEntities.some(
            (selectedEntity) =>
                selectedEntity.ObjectId === entity.ObjectId &&
                selectedEntity.instanceIndex === entity.instanceIndex &&
                selectedEntity.pathIndex === entity.pathIndex &&
                selectedEntity.drawKind === entity.drawKind,
        );
    };

    const handleAddLaser = async (
        position: vec3,
        normal: vec3,
        options: { attachedToPlane?: boolean; measureZ?: boolean; objectId?: ObjectId },
    ) => {
        const laserId = crypto.randomUUID();
        const { attachedToPlane, measureZ, objectId } = options;
        const laserAdded = await addLaser(position, normal, {
            laserId,
            measureZ,
            attachedToPlane,
            objectId,
        });
        if (laserAdded) {
            dispatch(
                undoRedoActions.push({
                    description: "Add laser measurement",
                    undoCallback: () => {
                        dispatch(clippingOutlineLaserActions.removeLaser(laserId));
                    },
                    redoCallback: () => {
                        void addLaser(position, normal, {
                            laserId,
                            measureZ,
                            attachedToPlane,
                            objectId,
                        });
                    },
                }),
            );
        }
    };

    const handleCanvasPick: MouseEventHandler<HTMLCanvasElement> = async (evt) => {
        const pointerDownState = pointerDownStateRef.current;
        const longPress = pointerDownState && evt.timeStamp - pointerDownState.timestamp >= 800;
        const drag =
            pointerDownState &&
            vec2.dist([pointerDownState.x, pointerDownState.y], [evt.nativeEvent.offsetX, evt.nativeEvent.offsetY]) >=
                5;

        if (!view || !canvas || longPress || drag) {
            return;
        }

        const mobileMeasurePickers = [Picker.Dimension, Picker.PointLine, Picker.OutlineLaser, Picker.Coordinates];
        if (isMobileMeasureInteraction && mobileMeasurePickers.includes(picker)) {
            return;
        }

        if (picker === Picker.Object && arcgisCanvasClickHandler && arcgisCanvasClickHandler(evt)) {
            return;
        }

        const planePicking = cameraType === CameraType.Orthographic && view.renderState.camera.far < 1;

        function unselectDimensionWithUndoRedo(entity: ExtendedMeasureEntity) {
            const entitiesToRestore = structuredClone(measure.selectedEntities[measure.currentIndex] || []);
            dispatch(measureActions.unselectEntity(entity));
            dispatch(
                undoRedoActions.push({
                    description: "Unselect dimension measurement",
                    undoCallback: () => dispatch(measureActions.setSelectedEntities(entitiesToRestore)),
                    redoCallback: () => dispatch(measureActions.unselectEntity(entity)),
                }),
            );
        }

        function addDimensionWithUndoRedo(entity: ExtendedMeasureEntity, shiftKey: boolean) {
            const previousEntities = structuredClone(measure.selectedEntities[measure.currentIndex] || []);
            const currentSet = measure.selectedEntities[measure.currentIndex] || [];
            const isReplace = !shiftKey;
            const isAddingSecond = currentSet.length === 1 && shiftKey;

            if (isReplace) {
                dispatch(measureActions.setSelectedEntities([entity]));
                dispatch(
                    undoRedoActions.push({
                        description: "Add dimension measurement",
                        undoCallback: () => dispatch(measureActions.setSelectedEntities(previousEntities)),
                        redoCallback: () => dispatch(measureActions.setSelectedEntities([entity])),
                    }),
                );
                return;
            }

            if (currentSet.length === 0) {
                dispatch(measureActions.selectEntity({ entity, pin: true }));
            } else {
                if (measure.pinned !== 0) {
                    dispatch(measureActions.pin(0));
                }
                dispatch(measureActions.selectEntity({ entity, pin: false }));
            }
            dispatch(
                undoRedoActions.push({
                    description: "Add dimension measurement",
                    undoCallback: () => dispatch(measureActions.setSelectedEntities(previousEntities)),
                    redoCallback: () => {
                        if (isAddingSecond) {
                            dispatch(measureActions.pin(0));
                        }
                        dispatch(
                            measureActions.selectEntity({
                                entity,
                                pin: isAddingSecond ? false : shiftKey,
                            }),
                        );
                    },
                }),
            );
        }

        pointerDownStateRef.current = undefined;

        const isTouch = evt.nativeEvent instanceof PointerEvent && evt.nativeEvent.pointerType === "touch";
        const shouldUseMobileSelectionContextMenu = !hasMouseSupport() && !isAppBuild();
        const useTouchPickRadius = isTouch || shouldUseMobileSelectionContextMenu;
        const pickOutline = measure.snapKind === "clippingOutline" && picker === Picker.Measurement;
        let result: Awaited<ReturnType<typeof view.pick>>;
        try {
            result = await view.pick(evt.nativeEvent.offsetX, evt.nativeEvent.offsetY, {
                pickCameraPlane,
                sampleDiscRadius: useTouchPickRadius ? 8 : 4,
                pickOutline,
            });
        } catch (error) {
            // GPU pick readback can throw (e.g. Invalid typed array length) if the
            // pick buffer is torn or undersized. Treat that as a miss so it does
            // not become an unhandled rejection.
            telemetry.trackCaughtException({
                source: "render.useCanvasClickHandler.pick",
                category: "render",
                error,
            });
            internalEventEmitter?.emit("intersectPointClick", null);
            return;
        }

        if (result?.position) {
            internalEventEmitter?.emit("intersectPointClick", result.position);
        } else {
            internalEventEmitter?.emit("intersectPointClick", null);
        }

        // Everything below goes by the picker this click started in. If another tool took over
        // while the pick was in flight, acting on the click would act for a tool that is no longer
        // open - such as a selection painted back over the one entering object-to-object cleared.
        if (selectPicker(store.getState()) !== picker) {
            return;
        }

        // Use lazyLastPickSample instead of mainObject, because if user has object selected prior -
        // first click will unselect it and mainObject will be undefined.
        // Using lastClickedObjectIdRef instead of lazyLastPickSample, because lastPickSample is also updated
        // on mouse move, which we don't want here.
        const clickCount = clickCountRef.registerClick();
        const isSameObjectPicked =
            result?.objectId &&
            result.objectId === lastClickedObjectIdRef.current &&
            !knownPdfObjectIds?.has(result.objectId);

        // Zoom on object on double click
        {
            if (result && isSameObjectPicked && picker === Picker.Object && clickCount >= 2) {
                if (clickCount === 2) {
                    // Zoom in on the object on double click, ignore subsequent clicks
                    abortDblClickZoom();
                    flyTo({ objectIds: [result.objectId], abortSignal: dblClickZoomAbortControllerRef.current.signal });
                }

                if (result.objectId === mainObject) {
                    // Return if main object is already selected to prevent unselecting it
                    return;
                }

                if (clickCount > 2) {
                    // Ignore 3+ clicks on the same object to avoid unselecting/selecting the object rapidly
                    return;
                }
            }
        }

        // The extra click of a double-click on one object is not a pick of its own. Without this, the
        // first click completes or dismisses the pair and the second, landing on the state that left,
        // dismisses the pair still being measured or starts the next one from the same object.
        //
        // Goes by the browser's own count rather than clickCount: that one runs on a 500ms window
        // that every click restarts, so it would also swallow a deliberate click on the same object
        // made just after - the unpick, the click that dismisses a quick result, or the first pick
        // after a dismiss. Picks resolve within a frame or two, so whether the two handlers overlap
        // says nothing about whether the clicks were a double-click.
        //
        // iOS does not count taps that way, so touch also goes by the time since the last tap that
        // acted on the same object. Only a tap that acts moves that mark, so an ignored one does not
        // hold back the tap after it.
        if (picker === Picker.ObjectMeasurement) {
            const lastTap = lastObjectMeasureTapRef.current;
            const isDoubleTap =
                useTouchPickRadius &&
                lastTap !== undefined &&
                lastTap.objectId === result?.objectId &&
                evt.timeStamp - lastTap.timeStamp < DOUBLE_TAP_MS;
            if (isSameObjectPicked && (evt.detail === 2 || isDoubleTap)) {
                return;
            }
            lastObjectMeasureTapRef.current =
                result?.objectId !== undefined ? { objectId: result.objectId, timeStamp: evt.timeStamp } : undefined;
        }

        lastClickedObjectIdRef.current = result?.objectId ?? null;

        async function pickMeasureEntity(result: PickSampleExt) {
            if (!view) {
                return;
            }
            const now = performance.now();
            dispatch(renderActions.addLoadingHandle(now));
            try {
                const tolerance = applyCameraDistanceToMeasureTolerance(
                    result.position,
                    view.renderState.camera,
                    measureHoverSettings,
                );
                const hover =
                    view.measure &&
                    (await view.measure.core.pickMeasureEntity(
                        result.objectId,
                        result.position,
                        tolerance,
                        allowGeneratedParametric.enabled,
                    ));
                return (
                    hover?.entity && {
                        ...hover.entity,
                        connectionPoint: hover.connectionPoint,
                    }
                );
            } finally {
                dispatch(renderActions.removeLoadingHandle(now));
            }
        }

        dispatchLastPickSample(lastPickSampleActions.set(result ?? null));

        // Once a pair is complete the next click only clears it, whatever it lands on - see
        // dismissObjectMeasure.
        if (picker === Picker.ObjectMeasurement && selectObjectMeasureIds(store.getState()).length >= 2) {
            dismissObjectMeasure(dispatch, store.getState());
            return;
        }

        if (
            !result ||
            picker === Picker.CrossSection ||
            picker === Picker.OutlineLaser ||
            picker === Picker.Coordinates
        ) {
            if (picker === Picker.Object) {
                dispatch(renderActions.setStamp(null));

                if (selectIsXRayActive(store.getState())) {
                    return;
                }

                if (!result) {
                    // Prevent clearing selection if a select box just completed
                    // This prevents the background click from clearing the selection immediately after select box release
                    if (
                        abortPendingSelectionForBackgroundClick(
                            isWithinSelectBoxGuardWindow(lastSelectBoxCompletionTime),
                            abortSharedSelection,
                        )
                    ) {
                        // Select box just completed, ignore this background click
                        return;
                    }

                    // Real background click (outside the same-mouseup guard): drop an
                    // in-flight box resolve so it cannot land on top of this clear.
                    invalidateSelectBoxAbort();

                    // A background click supersedes any in-flight object resolution. It was
                    // aborted above before clearing selection so a delayed metadata response
                    // cannot repaint the object selected by the earlier click.
                }

                if (!result && highlightedObjects.idArr.length > 0) {
                    // While an interrupted click's optimistic fragment is on screen, the live
                    // set is that temporary fragment. Undo must restore the pre-paint
                    // selection, and the orphaned snapshot must not outlive this click.
                    const pendingPaint = getPendingOptimisticPaint();
                    const previousSelection = snapshotUndoIds(
                        pendingPaint?.previousHighlightIds ?? highlightedObjects.idArr,
                    );
                    const previousMainObject = pendingPaint ? pendingPaint.previousMainObject : mainObject;
                    const previousMainObjects = pendingPaint ? pendingPaint.previousMainObjects : mainObjects;
                    discardPendingOptimisticPaint();
                    const clearSelection = () => {
                        // Set mainObject to undefined FIRST, then clear selection
                        // This prevents race conditions with components that react to mainObject changes
                        dispatch(renderActions.setMainObject(undefined));
                        dispatch(renderActions.clearMainObjects());
                        dispatchHighlighted(highlightActions.setIds([]));
                    };
                    clearSelection();

                    dispatch(
                        undoRedoActions.push({
                            description: "Clear selection",
                            undoCallback: () => {
                                // When undoing, set selection FIRST, then mainObject
                                dispatchHighlighted(highlightActions.setIds(restoreUndoIds(previousSelection)));
                                if (previousMainObject !== undefined) {
                                    dispatch(renderActions.setMainObject(previousMainObject));
                                }
                                dispatch(renderActions.setMainObjects(previousMainObjects));
                            },
                            redoCallback: clearSelection,
                        }),
                    );
                }

                // Deselect marker when clicking on background (empty space)
                if (!result && selectedMarker) {
                    deselectMarkerWithCallback(selectedMarker, markerCollections, dispatch);
                }

                return;
            } else if (picker === Picker.Measurement) {
                if (measure.hover) {
                    dispatch(
                        measureActions.selectEntity({
                            entity: planePicking
                                ? {
                                      ...measure.hover,
                                      settings: { planeMeasure: view.renderState.clipping.planes[0]?.normalOffset },
                                  }
                                : measure.hover,
                            pin: evt.shiftKey,
                        }),
                    );
                }
                return;
            } else if (picker === Picker.Dimension) {
                if (measure.hover && !measureObjectIsVertex(measure.hover)) {
                    const entity = planePicking
                        ? {
                              ...measure.hover,
                              settings: { planeMeasure: view.renderState.clipping.planes[0]?.normalOffset },
                          }
                        : measure.hover;

                    if (isEntitySelected(entity)) {
                        unselectDimensionWithUndoRedo(entity);
                        return;
                    }

                    addDimensionWithUndoRedo(entity, evt.shiftKey);
                }
                return;
            } else if (picker === Picker.Area) {
                if (measureObjectIsVertex(measure.hover) && planes.length) {
                    dispatch(areaActions.addPt(measure.hover.parameter, view));
                }
                return;
            } else if (picker === Picker.PointLine) {
                if (measureObjectIsVertex(measure.hover)) {
                    await addPointToPointLine({
                        position: measure.hover.parameter,
                        position2d: [evt.nativeEvent.offsetX, evt.nativeEvent.offsetY],
                        objectId: measure.hover.ObjectId,
                        measureEntity: measure.hover,
                    });
                }
                return;
            } else if (picker === Picker.CrossSection) {
                const position =
                    result?.position ??
                    view.convert.screenSpaceToWorldSpace([
                        vec2.fromValues(evt.nativeEvent.offsetX, evt.nativeEvent.offsetY),
                    ])[0];

                if (!position) {
                    return;
                }

                if (!crossSectionPoint) {
                    dispatch(orthoCamActions.setCrossSectionPoint(position));
                    return;
                }

                if (newDesign) {
                    openWidget(featuresConfig.crossSection.key);
                }
                const mat = mat3.fromQuat(mat3.create(), view.renderState.camera.rotation);
                const up = vec3.fromValues(0, 0, 1);
                const topDown = vec3.equals(vec3.fromValues(mat[6], mat[7], mat[8]), up);
                const pos = topDown
                    ? vec3.fromValues(position[0], position[1], crossSectionPoint[2])
                    : vec3.copy(vec3.create(), position);

                const right = vec3.sub(vec3.create(), crossSectionPoint, pos);
                const l = vec3.len(right);
                vec3.scale(right, right, 1 / l);
                const p = vec3.scaleAndAdd(vec3.create(), crossSectionPoint, right, l / -2);
                let dir = vec3.cross(vec3.create(), up, right);

                if (topDown) {
                    const midPt = (view.measure?.draw.toMarkerPoints([p]) ?? [])[0];
                    if (midPt && midPt[0] >= 0 && midPt[0] < size.width && midPt[1] >= 0 && midPt[1] < size.height) {
                        // Why doing a pick here? People often put first and second cross section points on opposite sides of the road
                        // and they can be outside the road, so they don't represent relevant elevation.
                        // By doing this pick we try to put the camera at the proper elevation.
                        const midPick = await view.pick(midPt[0], midPt[1], {
                            sampleDiscRadius: 100,
                            searchType: "closestToPickCenter",
                        });
                        if (midPick) {
                            vec3.copy(p, midPick.position);
                        }
                    }
                } else if (right[1] < 0.01) {
                    right[1] = 0;
                    dir = vec3.clone(up);
                    vec3.cross(up, right, dir);
                    vec3.normalize(up, up);
                } else {
                    vec3.normalize(dir, dir);
                }
                vec3.cross(right, up, dir);
                vec3.normalize(right, right);

                if (!newDesign) {
                    const rotation = quat.fromMat3(
                        quat.create(),
                        mat3.fromValues(right[0], right[1], right[2], up[0], up[1], up[2], dir[0], dir[1], dir[2]),
                    );

                    dispatch(
                        renderActions.setCamera({
                            type: CameraType.Orthographic,
                            goTo: {
                                position: p,
                                rotation,
                                fov: 45,
                                far: crossSectionClipping,
                            },
                            gridOrigo: p,
                        }),
                    );
                }
                const w = vec3.dot(dir, p);
                dispatch(
                    renderActions.setClippingPlanes({
                        enabled: true,
                        planes: [
                            {
                                normalOffset: [dir[0], dir[1], dir[2], w],
                                baseW: w,
                                color: [0, 1, 0, 0.2],
                                anchorPos: p,
                            },
                        ],
                    }),
                );
                dispatch(renderActions.setPicker(Picker.Object));
                dispatch(orthoCamActions.setCrossSectionPoint(undefined));
                dispatch(orthoCamActions.setCrossSectionHover(undefined));
                return;
            } else if (picker === Picker.OutlineLaser) {
                const cameraAlignedPlane = findCameraAlignedPlane(view);
                const isOrtho = cameraType === CameraType.Orthographic;
                if (isOrtho && cameraAlignedPlane && viewMode === ViewMode.CrossSection) {
                    const normal = vec3.fromValues(
                        cameraAlignedPlane.normalOffset[0],
                        cameraAlignedPlane.normalOffset[1],
                        cameraAlignedPlane.normalOffset[2],
                    );
                    const position = view.convert.screenSpaceToWorldSpace([
                        vec2.fromValues(evt.nativeEvent.offsetX, evt.nativeEvent.offsetY),
                    ])[0];
                    if (!position) {
                        return;
                    }
                    await handleAddLaser(position, normal, { attachedToPlane: true, measureZ: false });
                } else if (result) {
                    await handleAddLaser(result.position, result.normal, {
                        objectId: result.objectId,
                        measureZ: !(isOrtho && cameraAlignedPlane),
                    });
                }
                return;
            } else if (picker === Picker.Coordinates) {
                if (result) {
                    const coordinate = await addCoordinate(result.position, {
                        screenOffset: {
                            x: result.x,
                            y: result.y,
                        },
                        normal: result.normal,
                        coordinateId: crypto.randomUUID(),
                        objectId: result.objectId,
                    });
                    if (coordinate) {
                        dispatch(
                            undoRedoActions.push({
                                description: "Add coordinate",
                                undoCallback: () => {
                                    dispatch(coordinatesActions.removeCoordinate(coordinate.id));
                                },
                                redoCallback: () => {
                                    dispatch(coordinatesActions.addCoordinate(coordinate));
                                },
                            }),
                        );
                    }
                }
                return;
            } else {
                return;
            }
        }

        const normal =
            isRealVec([...result.normal]) && vec3.dot(result.normal, result.normal) !== 0
                ? vec3.clone(result.normal)
                : undefined;
        const planeNormal = getPlaneCreationNormal(result.normal);
        const position = vec3.clone(result.position);

        async function fetchPlaneMetadata(objectId: number | undefined, normal: vec3, searchBigEnoughBounds: boolean) {
            let rotation = 0;
            let wBounds;
            const getBounds = searchBigEnoughBounds ? getBigEnoughObjectBoundsW : getObjectBoundsW;
            try {
                if (objectId !== undefined && dataAccess) {
                    const [rotationQuat, bounds] = await Promise.all([
                        getObjectMetadataRotation(dataAccess, objectId),
                        getBounds(dataAccess, objectId, normal),
                    ]);
                    wBounds = bounds;
                    if (rotationQuat) {
                        rotation = getLocalRotationAroundNormal(rotationQuat, normal);
                    }
                }
            } catch (ex) {
                console.warn("Error getting object metadata for plane", ex);
                telemetry.trackCaughtException({
                    source: "render.useCanvasClickHandler.planeMetadata",
                    category: "render",
                    error: ex,
                });
            }
            return { rotation, wBounds, w: vec3.dot(normal, position) };
        }

        if (picker === Picker.FormLocation) {
            if (result) {
                placeLocationForm({ location: position });
                dispatch(renderActions.stopPicker(Picker.FormLocation));
            }
        } else if (picker === Picker.JiraIssuePosition) {
            if (result) {
                dispatch(jiraActions.setNewIssuePosition(position));
                dispatch(renderActions.stopPicker(Picker.JiraIssuePosition));
            }
            return;
        } else if (picker === Picker.Object) {
            if (!canvasSelectionEnabled || selectIsXRayActive(store.getState())) {
                return;
            }

            // Ignore the click that immediately follows a pin placement.
            if (isWithinMarkerCreationSuppressionWindow()) {
                return;
            }

            const abortSignal = abortSharedSelection();

            // The abort above orphans an interrupted click's optimistic fragment (its
            // abort path returns without restoring). Early returns below that end the
            // interaction must put the pre-paint selection back so the fragment does
            // not stick around with its snapshot pending.
            const restoreInheritedOptimisticPaint = () => {
                const orphanedPaint = getPendingOptimisticPaint();
                if (orphanedPaint) {
                    dispatch(renderActions.setMainObject(orphanedPaint.previousMainObject));
                    dispatch(renderActions.setMainObjects(orphanedPaint.previousMainObjects));
                    dispatchHighlighted(highlightActions.setIds(orphanedPaint.previousHighlightIds));
                    discardPendingOptimisticPaint();
                }
            };

            if (
                cameraState.type === CameraType.Orthographic &&
                result.pointFactor !== undefined &&
                [ViewMode.CrossSection, ViewMode.FollowPath, ViewMode.Deviations].includes(viewMode)
            ) {
                dispatch(
                    renderActions.setStamp({
                        kind: StampKind.Deviation,
                        mouseX: evt.nativeEvent.offsetX,
                        mouseY: evt.nativeEvent.offsetY,
                        pinned: false,
                        data: {
                            deviation: result.pointFactor,
                        },
                    }),
                );
                return;
            }

            if (locationFormAssetClickHandler(result)) {
                return;
            }

            if (result.objectId === -1 || (!terrain.canSelect && isTerrainObjectId(result.objectId))) {
                restoreInheritedOptimisticPaint();
                dispatch(renderActions.setStamp(null));
                return;
            }

            // Prevent object clicks from interfering with select box completion
            // When select box is released over an object, both finalize and click fire
            // We need to ignore the click to preserve the select box results
            if (isWithinSelectBoxGuardWindow(lastSelectBoxCompletionTime)) {
                // Select box just completed, ignore this object click
                return;
            }

            // Real object click after the same-mouseup window: drop the in-flight box
            // so its resolve cannot overwrite this pick.
            invalidateSelectBoxAbort();

            if (!db || !dataAccess) {
                return;
            }

            if (shouldUseMobileSelectionContextMenu && consumeMobileMenuDismissed()) {
                dispatch(renderActions.setMainObject(undefined));
                dispatch(renderActions.clearMainObjects());
                dispatchHighlighted(highlightActions.setIds([]));
                return;
            }

            // PDFs are excluded from the Procore object tree (see skipPdfFiles in
            // features/modelTree). They still render in 3D, but must never be selectable —
            // not even via the optimistic highlight below. Hybrid gate:
            // 1) sync early-exit for ids already known to be PDFs
            // 2) on Procore, await the PDF check before any paint for unknown ids
            // Selection-target resolution still starts in parallel with (2) so non-PDF clicks
            // keep the same total latency; only the optimistic paint waits on the PDF check.
            if (knownPdfObjectIds?.has(result.objectId)) {
                restoreInheritedOptimisticPaint();
                return;
            }

            // Re-click of a mesh that's already in the highlight set. The current
            // ids are already the resolved group (parent + fragments), so skip
            // First/Last resolve and the optimistic wipe → redraw → deselect flash.
            // Use an in-flight optimistic paint's pre-paint snapshot as the baseline
            // so a right-click (or another left-click) that interrupts resolve does
            // not treat the temporary mesh as already selected.
            const inheritedPaint = selectMultiple ? null : getPendingOptimisticPaint();
            const baselineMainObject = inheritedPaint?.previousMainObject ?? mainObject;
            const baselineMainObjects = inheritedPaint?.previousMainObjects ?? mainObjects;
            const baselineHighlightIds = inheritedPaint?.previousHighlightIds ?? Array.from(highlightedObjects.idArr);
            const baselineHighlightSet = new Set(baselineHighlightIds);

            if (!selectMultiple && baselineHighlightSet.has(result.objectId)) {
                discardPendingOptimisticPaint();
                // Undo must restore the pre-paint selection, not the live set: while an
                // interrupted click's optimistic paint is on screen, the live set is its
                // temporary [clickedMesh] fragment rather than what the user had selected.
                const groupIds = baselineHighlightIds.slice();
                const deselectObject = () => {
                    dispatch(renderActions.setMainObject(undefined));
                    dispatch(renderActions.clearMainObjects());
                    dispatch(renderActions.setStamp(null));
                    dispatchHighlighted(highlightActions.setIds([]));
                    if (secondaryHighlightProperty) {
                        abortSecondaryHighlight();
                        dispatchHighlightCollections(
                            highlightCollectionsActions.setIds(HighlightCollection.SecondaryHighlight, []),
                        );
                        currentSecondaryHighlightQuery.current = "";
                    }
                };
                deselectObject();
                dispatch(
                    undoRedoActions.push({
                        description: "Select already selected object",
                        undoCallback: () => {
                            dispatchHighlighted(highlightActions.setIds(groupIds));
                            dispatch(renderActions.setMainObject(baselineMainObject));
                            dispatch(renderActions.setMainObjects(baselineMainObjects));
                        },
                        redoCallback: deselectObject,
                    }),
                );
                return;
            }

            const selectionTargetPromise = resolveSelectionTarget({
                dataAccess,
                objectId: result.objectId,
                resolution: selectionResolution,
                abortSignal,
                classAware: nodeClassSelectionEnabled,
            });

            if (isProcoreDesign) {
                const pickedNonSelectablePdf = await isPdfOrDrawingObjectId(dataAccess, result.objectId, {
                    knownPdfObjectIds,
                    signal: abortSignal,
                });
                if (abortSignal.aborted) {
                    return;
                }
                if (pickedNonSelectablePdf) {
                    restoreInheritedOptimisticPaint();
                    return;
                }
            }

            if (selectIsXRayActive(store.getState())) {
                // An isolation turned X-ray on while the PDF check was in flight and has
                // already replaced the highlight set. Drop the click without restoring an
                // inherited pre-paint baseline — that would overwrite the isolation.
                discardPendingOptimisticPaint();
                return;
            }

            // Instant pre-PR selection: highlight the clicked object immediately (zero I/O) so the
            // click feels as responsive as before NodeClass resolution, then repaint below only if
            // the resolved first/last-object target actually differs (rolls up to an outer object
            // and/or adds descendant geometry).
            //
            // `setIds` replaces the whole highlight set, so this optimistic paint wipes any existing
            // selection. That is correct for the common "replace selection with a new object" case,
            // but wrong when the click actually resolves to a toggle/deselect of an already-selected
            // object (e.g. clicking a fragment of an object selected via search). To get instant
            // feedback without losing data in those cases, we snapshot the pre-click selection here
            // (via the `highlightedObjects`/`mainObject` closures) and restore it after resolution
            // if the click turns out to target an already-selected object — see below.
            //
            // Trade-off: when a non-empty selection already exists and resolution needs metadata I/O,
            // the wiped `{clicked}` state can render for that I/O window before the restore lands, so
            // clicking one of several selected objects to deselect it may briefly flash. We accept
            // that for instant feedback on the far more common replace-selection click.
            // On Procore, paint is deferred until the PDF check above completes so PDFs never flash.
            const optimisticallyPainted = !selectMultiple;
            if (optimisticallyPainted) {
                setPendingOptimisticPaint({
                    ownerGeneration: beginOptimisticPaintAttempt(),
                    previousMainObject: baselineMainObject,
                    previousMainObjects: baselineMainObjects,
                    previousHighlightIds: baselineHighlightIds,
                });
                dispatch(renderActions.setMainObject(result.objectId));
                dispatch(renderActions.setMainObjects([result.objectId]));
                dispatchHighlighted(highlightActions.setIds([result.objectId]));
            }

            const selectionTarget = await selectionTargetPromise;

            if (abortSignal.aborted) {
                return;
            }

            if (selectIsXRayActive(store.getState())) {
                // Same as after the PDF check: an isolation landed mid-resolve and owns the
                // highlight set now, so neither the resolved target nor the pre-paint
                // baseline may be written over it.
                discardPendingOptimisticPaint();
                return;
            }

            discardPendingOptimisticPaint();

            // Remember the clicked surface for this object so a later ortho switch can align its reveal
            // clip plane to the picked face. Keyed to the resolved main object; consumers ignore it when
            // the id no longer matches the current selection (see getOrthoFitClipPlane).
            dispatch(
                renderActions.setMainObjectPick(
                    result.sampleType === "surface" && isRealVec([...result.normal])
                        ? {
                              objectId: selectionTarget.mainObjectId,
                              position: Array.from(result.position) as [number, number, number],
                              normal: Array.from(result.normal) as [number, number, number],
                          }
                        : null,
                ),
            );

            const alreadySelected = baselineHighlightSet.has(selectionTarget.mainObjectId);

            // If the click resolves to an already-selected object, the optimistic replace above was
            // wrong: it wiped the prior selection that the toggle/deselect/clear branches below
            // expect as their base (they apply `remove`/delta operations on the live highlight set).
            // Restore the pre-click selection first. This restore and the branch's own dispatch run
            // in the same synchronous tick after the await, so React batches them into a single
            // render — there is no flash between the restored selection and the final state (any
            // transient flash happens earlier, during the optimistic-wipe I/O window noted above).
            if (optimisticallyPainted && alreadySelected) {
                dispatch(renderActions.setMainObject(baselineMainObject));
                dispatch(renderActions.setMainObjects(baselineMainObjects));
                dispatchHighlighted(highlightActions.setIds(baselineHighlightIds));
            }

            if (selectMultiple) {
                if (alreadySelected) {
                    const deselectObject = () => {
                        if (selectionTarget.mainObjectId === mainObject) {
                            dispatch(renderActions.setMainObject(undefined));
                        }
                        dispatch(renderActions.removeFromMainObjects([selectionTarget.mainObjectId]));
                        dispatchHighlighted(highlightActions.remove(selectionTarget.highlightIds));
                    };
                    deselectObject();

                    dispatch(
                        undoRedoActions.push({
                            description: "Multiselect already selected object",
                            undoCallback: () => {
                                if (selectionTarget.mainObjectId === mainObject) {
                                    dispatch(renderActions.setMainObject(selectionTarget.mainObjectId));
                                }
                                dispatch(renderActions.addToMainObjects([selectionTarget.mainObjectId]));
                                dispatchHighlighted(highlightActions.add(selectionTarget.highlightIds));
                            },
                            redoCallback: deselectObject,
                        }),
                    );
                } else {
                    const selectObject = () => {
                        dispatch(renderActions.setMainObject(selectionTarget.mainObjectId));
                        dispatch(renderActions.addToMainObjects([selectionTarget.mainObjectId]));
                        dispatchHighlighted(highlightActions.add(selectionTarget.highlightIds));

                        // Deselect marker when multiselecting objects
                        if (selectedMarker) {
                            deselectMarkerWithCallback(selectedMarker, markerCollections, dispatch);
                        }
                    };
                    selectObject();
                    dispatch(
                        undoRedoActions.push({
                            description: "Multiselect object",
                            undoCallback: () => {
                                dispatch(renderActions.setMainObject(mainObject));
                                dispatch(renderActions.removeFromMainObjects([selectionTarget.mainObjectId]));
                                dispatchHighlighted(highlightActions.remove(selectionTarget.highlightIds));
                            },
                            redoCallback: selectObject,
                        }),
                    );
                }
            } else if (alreadySelected && isProcoreDesign) {
                const deselectObject = () => {
                    if (selectionTarget.mainObjectId === baselineMainObject) {
                        dispatch(renderActions.setMainObject(undefined));
                        dispatch(renderActions.setStamp(null));
                    }
                    dispatch(renderActions.removeFromMainObjects([selectionTarget.mainObjectId]));
                    dispatchHighlighted(highlightActions.remove(selectionTarget.highlightIds));

                    if (!secondaryHighlightProperty) {
                        return;
                    }

                    abortSecondaryHighlight();
                };
                deselectObject();

                dispatch(
                    undoRedoActions.push({
                        description: "Select already selected object",
                        undoCallback: () => {
                            dispatchHighlighted(highlightActions.add(selectionTarget.highlightIds));
                            dispatch(renderActions.setMainObject(selectionTarget.mainObjectId));
                            dispatch(renderActions.addToMainObjects([selectionTarget.mainObjectId]));
                        },
                        redoCallback: deselectObject,
                    }),
                );
            } else if (alreadySelected && selectionTarget.mainObjectId === baselineMainObject) {
                dispatch(renderActions.setMainObject(undefined));
                dispatch(renderActions.removeFromMainObjects([selectionTarget.mainObjectId]));
                dispatch(renderActions.setStamp(null));
                dispatchHighlighted(highlightActions.remove(selectionTarget.highlightIds));

                if (!secondaryHighlightProperty) {
                    return;
                }

                abortSecondaryHighlight();
                dispatchHighlightCollections(
                    highlightCollectionsActions.setIds(HighlightCollection.SecondaryHighlight, []),
                );
                currentSecondaryHighlightQuery.current = "";
            } else {
                const previousSelection = snapshotUndoIds(baselineHighlightIds);
                const selectObject = () => {
                    dispatch(renderActions.setMainObject(selectionTarget.mainObjectId));
                    if (!alreadySelected) {
                        dispatch(renderActions.setMainObjects([selectionTarget.mainObjectId]));
                        dispatchHighlighted(highlightActions.setIds(selectionTarget.highlightIds));
                    } else if (baselineHighlightIds.length > 1) {
                        // Clicking already-selected object without modifier: clear all selections
                        dispatch(renderActions.setMainObject(undefined));
                        dispatch(renderActions.clearMainObjects());
                        dispatchHighlighted(highlightActions.setIds([]));
                    }

                    // Deselect marker when selecting a 3D object
                    if (selectedMarker) {
                        deselectMarkerWithCallback(selectedMarker, markerCollections, dispatch);
                    }
                };

                // The instant pre-PR paint above already selected exactly the clicked object. Skip
                // the repaint when the resolved target matches it (e.g. a leaf object, or a scene
                // with no NodeClass), but still run the side effects (marker deselect). `selectObject`
                // remains the full redo handler so redo re-applies the resolved selection correctly.
                const finalMatchesInstantPaint =
                    optimisticallyPainted &&
                    !alreadySelected &&
                    selectionTarget.mainObjectId === result.objectId &&
                    selectionTarget.highlightIds.length === 1;
                if (finalMatchesInstantPaint) {
                    if (selectedMarker) {
                        deselectMarkerWithCallback(selectedMarker, markerCollections, dispatch);
                    }
                } else {
                    selectObject();
                }
                dispatch(
                    undoRedoActions.push({
                        description: "Select object",
                        undoCallback: () => {
                            dispatch(renderActions.setMainObject(baselineMainObject));
                            dispatch(renderActions.setMainObjects(baselineMainObjects));
                            dispatchHighlighted(highlightActions.setIds(restoreUndoIds(previousSelection)));
                        },
                        redoCallback: selectObject,
                    }),
                );

                // On mobile Procore web, open the canvas context menu on pick-selection
                // instead of the previous long-press flow. Only applies when the tap
                // actually selects a new object (not when it deselects an existing one).
                // Desktop and standalone app builds keep their existing flows.
                const openTouchContextMenu = shouldUseMobileSelectionContextMenu && !alreadySelected;
                if (openTouchContextMenu) {
                    showContextMenuStamp(
                        [evt.nativeEvent.offsetX, evt.nativeEvent.offsetY],
                        result,
                        selectionTarget.mainObjectId,
                    );
                }

                if ((!showPropertiesStamp && !secondaryHighlightProperty) || !db) {
                    return;
                }

                const metadata = await (view.data
                    ? view.data.getObjectMetaData(selectionTarget.mainObjectId)
                    : db.getObjectMetdata(selectionTarget.mainObjectId));

                const hierarchyMeta = [metadata];
                try {
                    const iter = db.search(
                        {
                            full: true,
                            parentPath: metadata.path,
                            descentDepth: getAncestorSearchDepth(metadata.path),
                        },
                        undefined,
                    );
                    for await (const obj of iter) {
                        hierarchyMeta.push(await obj.loadMetaData());
                    }
                    hierarchyMeta.sort((a, b) => b.path.length - a.path.length);
                } catch (e) {
                    console.warn("Error getting hierarchy metadata", e);
                    telemetry.trackCaughtException({
                        source: "render.useCanvasClickHandler.hierarchyMetadata",
                        category: "render",
                        error: e,
                    });
                }

                if (showPropertiesStamp && !isProcoreDesign && !openTouchContextMenu) {
                    dispatch(
                        renderActions.setStamp({
                            kind: StampKind.Properties,
                            objectIds: hierarchyMeta.map((obj) => obj.id),
                            properties: hierarchyMeta.map((obj) => [
                                ["Name", obj.name],
                                ["Path", obj.path],
                                ...obj.properties,
                            ]),
                            mouseX: evt.nativeEvent.offsetX,
                            mouseY: evt.nativeEvent.offsetY,
                            pinned: true,
                        }),
                    );
                }

                if (!secondaryHighlightProperty) {
                    return;
                }

                const property = metadata.properties.find((prop) => prop[0] === secondaryHighlightProperty);
                const query = property?.[1];

                if (
                    query &&
                    query === currentSecondaryHighlightQuery.current &&
                    highlightCollections.secondaryHighlight.idArr.length
                ) {
                    return;
                }

                abortSecondaryHighlight();
                dispatchHighlightCollections(
                    highlightCollectionsActions.setIds(HighlightCollection.SecondaryHighlight, []),
                );

                if (!query) {
                    return;
                }

                const abortSignal = secondaryHighlightAbortController.current.signal;
                const loadingHandle = evt.timeStamp;
                currentSecondaryHighlightQuery.current = query;
                dispatch(renderActions.addLoadingHandle(loadingHandle));

                try {
                    const res: number[] = [];

                    const baseSearchProps = {
                        db,
                        abortSignal,
                        searchPatterns: [{ property: secondaryHighlightProperty, value: query, exact: true }],
                    };

                    if (metadata.type === NodeType.Internal) {
                        await searchDeepByPatterns({
                            ...baseSearchProps,
                            callback: (ids) => appendInPlace(res, ids),
                        });
                    } else {
                        await searchByPatterns({
                            ...baseSearchProps,
                            callback: (refs) => appendInPlace(res, extractObjectIds(refs)),
                        });
                    }

                    dispatchHighlightCollections(
                        highlightCollectionsActions.setIds(HighlightCollection.SecondaryHighlight, res),
                    );
                } catch (e) {
                    if (!abortSignal.aborted) {
                        console.warn(e);
                        telemetry.trackCaughtException({
                            source: "render.useCanvasClickHandler.secondaryHighlight",
                            category: "render",
                            error: e,
                        });
                    }
                } finally {
                    dispatch(renderActions.removeLoadingHandle(loadingHandle));
                }
            }
        } else if (picker === Picker.ClippingPlane) {
            if (!planeNormal || result.sampleType !== "surface") {
                return;
            }

            const { rotation, wBounds, w } = await fetchPlaneMetadata(result.objectId, planeNormal, true);

            dispatch(renderActions.setSectionPlaneHoverObjectId(null));
            dispatch(renderActions.setPicker(Picker.Object));
            dispatch(
                renderActions.addClippingPlane({
                    normalOffset: vec4.fromValues(planeNormal[0], planeNormal[1], planeNormal[2], w) as Vec4,
                    baseW: w,
                    rotation,
                    anchorPos: result?.position,
                    showPlane: false,
                    wBounds,
                }),
            );
        } else if (picker === Picker.SectionPlane) {
            if (!planeNormal || result.sampleType !== "surface") {
                return;
            }

            const snappedNormal = snapSectionPlaneNormalToAxis(planeNormal);
            // The pick alone places the plane, so it goes up on this tick. Object bounds
            // are a metadata round-trip that only refines the slider range, so they are
            // backfilled once the cut is already on screen.
            const w = vec3.dot(snappedNormal, position);

            dispatch(renderActions.setSectionPlaneHoverObjectId(null));

            if (isProcoreDesign) {
                const MAX_SECTION_PLANES = 6;
                if (planes.length >= MAX_SECTION_PLANES) {
                    if (!sectionPlaneLimitReached) {
                        dispatch(renderActions.setSectionPlaneLimitReached(true));
                    }
                    return;
                }
            }

            // Pin the create-origin to the anchor's projected position (gizmo position).
            const [anchorScreen] = result?.position
                ? view.convert.worldSpaceToScreenSpace([result.position], { round: false })
                : [];

            const boundsRequestId = addClippingPlaneWithUndo(
                {
                    normalOffset: vec4.fromValues(snappedNormal[0], snappedNormal[1], snappedNormal[2], w),
                    baseW: w,
                    anchorPos: result?.position,
                    showPlane: false,
                },
                {
                    screenPoint:
                        anchorScreen && Number.isFinite(anchorScreen[0]) && Number.isFinite(anchorScreen[1])
                            ? vec2.fromValues(anchorScreen[0], anchorScreen[1])
                            : vec2.fromValues(evt.nativeEvent.offsetX, evt.nativeEvent.offsetY),
                },
            );

            void backfillPlaneBounds({
                dispatch,
                dataAccess,
                objectId: result.objectId,
                boundsRequestId,
                normal: snappedNormal,
            });
        } else if (picker === Picker.OrthoPlane) {
            if (!normal || result.sampleType !== "surface") {
                return;
            }

            dispatch(
                renderActions.setCamera({
                    type: CameraType.Orthographic,
                    goTo: {
                        position,
                        rotation: rotationFromDirection(normal),
                        fov: 50,
                    },
                }),
            );
            dispatch(renderActions.setPicker(Picker.Object));
        } else if (picker === Picker.Measurement) {
            if (measure.hover) {
                dispatch(
                    measureActions.selectEntity({
                        entity: planePicking
                            ? {
                                  ...measure.hover,
                                  settings: { planeMeasure: view.renderState.clipping.planes[0]?.normalOffset },
                              }
                            : measure.hover,
                        pin: evt.shiftKey,
                    }),
                );
            } else if (measure.snapKind === "clippingOutline") {
                const pointEntity: ExtendedMeasureEntity = {
                    drawKind: "vertex",
                    ObjectId: result.objectId,
                    parameter: result.position,
                };
                dispatch(
                    measureActions.selectEntity({
                        entity: pointEntity,
                        pin: evt.shiftKey,
                    }),
                );
            } else {
                dispatch(measureActions.setLoadingBrep(true));
                const tolerance = applyCameraDistanceToMeasureTolerance(
                    result.position,
                    view.renderState.camera,
                    measurePickSettings,
                );
                const entity = await view.measure?.core.pickMeasureEntity(
                    result.objectId,
                    position,
                    tolerance,
                    allowGeneratedParametric.enabled,
                );
                if (entity?.entity) {
                    dispatch(
                        measureActions.selectEntity({
                            entity: entity.entity,
                            pin: evt.shiftKey,
                        }),
                    );
                }
                dispatch(measureActions.setLoadingBrep(false));
            }
        } else if (picker === Picker.ObjectMeasurement) {
            if (result.objectId !== undefined) {
                dispatch(measureActions.pickMeasureObject(result.objectId));
                // triangles can provide a backup if BREPs are not available
                // if copied on pick, they are guaranteed to be on screen and available
                const triangles = await queryView(view, "getObjectTriangles", result.objectId);
                if (triangles) {
                    await view.measure?.core.setObjectTriangles(result.objectId, triangles);
                }
            }
        } else if (picker === Picker.Dimension) {
            if (measure.hover && !measureObjectIsVertex(measure.hover)) {
                const entity = planePicking
                    ? {
                          ...measure.hover,
                          settings: { planeMeasure: view.renderState.clipping.planes[0]?.normalOffset },
                      }
                    : measure.hover;

                if (isEntitySelected(entity)) {
                    unselectDimensionWithUndoRedo(entity);
                    return;
                }

                addDimensionWithUndoRedo(entity, evt.shiftKey);
            } else if (measure.snapKind === "clippingOutline") {
                const pointEntity: ExtendedMeasureEntity = {
                    drawKind: "vertex",
                    ObjectId: result.objectId,
                    parameter: result.position,
                };

                if (isEntitySelected(pointEntity)) {
                    unselectDimensionWithUndoRedo(pointEntity);
                    return;
                }

                addDimensionWithUndoRedo(pointEntity, evt.shiftKey);
            } else {
                dispatch(measureActions.setLoadingBrep(true));
                const tolerance = applyCameraDistanceToMeasureTolerance(
                    result.position,
                    view.renderState.camera,
                    measurePickSettings,
                );
                const entity = await view.measure?.core.pickMeasureEntity(
                    result.objectId,
                    position,
                    tolerance,
                    allowGeneratedParametric.enabled,
                );
                if (entity?.entity && !measureObjectIsVertex(entity.entity)) {
                    if (isEntitySelected(entity?.entity)) {
                        unselectDimensionWithUndoRedo(entity.entity as ExtendedMeasureEntity);
                        return;
                    }

                    addDimensionWithUndoRedo(entity.entity as ExtendedMeasureEntity, evt.shiftKey);
                }
                dispatch(measureActions.setLoadingBrep(false));
            }
        } else if (picker === Picker.Manhole) {
            if (result.objectId === -1) {
                return;
            }
            dispatch(manholeActions.selectObj({ id: result.objectId, pos: position }));
        } else if (picker === Picker.FollowPathObject) {
            if (result.objectId === -1 || !view.measure) {
                return;
            }

            // User may either select an alignment or a random curve without an alignment
            // Alignment will have (alignment, fpObj, stationInfo)
            // Random curve - only fpObj
            const fpObj = await view.measure.followPath.followParametricObjects([result.objectId]);
            if (!fpObj) {
                return;
            }

            const alignment = await view.measure?.road.getAlignment(result.objectId);
            const stationInfo = alignment
                ? view.measure.road.getStationInfoAtPoint(alignment, result.position)
                : undefined;

            dispatch(renderActions.stopPicker(Picker.FollowPathObject));
            dispatch(alignmentActions.setFollowObject(fpObj));
            dispatch(alignmentActions.setAlignment({ id: result.objectId, name: USER_SELECTED_ALIGNMENT_NAME }));
            goToStation({
                fpObj,
                station: stationInfo?.station ?? view.measure.followPath.getClosestStation(fpObj, result.position) ?? 0,
                keepOffset: false,
                updateStationInfo: true,
            });
        } else if (picker === Picker.Area) {
            if (measureObjectIsVertex(measure.hover)) {
                dispatch(areaActions.addPt(measure.hover.parameter, view));
            } else {
                dispatch(areaActions.addPt(position, view));
            }
        } else if (picker === Picker.PointLine) {
            await addPointToPointLine({
                position: result.position,
                position2d: [evt.nativeEvent.offsetX, evt.nativeEvent.offsetY],
                objectId: result.objectId,
                measureEntity: measure.hover ?? (await pickMeasureEntity(result)),
            });
        } else if (picker === Picker.SdkPicking) {
            const session = pickingSessionRef.current;
            if (!session) {
                return;
            }
            // Recompute the snap from the hovered entity so the resolved position matches the
            // lock-on shown on hover (same getSnapInfo path used by the PointLine tool). We rely on
            // measure.hover (picked with the session's snap tolerance) rather than re-picking with a
            // default tolerance, so only the enabled snap types are ever considered.
            const measureEntity = measure.hover;
            let snapInfo: PointSnapInfo | undefined;
            if (measureEntity?.connectionPoint) {
                snapInfo = await getSnapInfo({
                    view,
                    entity: measureEntity,
                    connectionPoint: measureEntity.connectionPoint,
                    corners: true,
                });
            }
            const adjacentVertices =
                snapInfo && measureEntity ? await getSnapAdjacentVertices(view, measureEntity, snapInfo) : undefined;
            session.resolve({
                position: snapInfo?.connection ?? result.position,
                normal: result.normal,
                snapType: (snapInfo?.type ?? "none") as PickingSnapType,
                internalObjectId: result.objectId === -1 ? null : result.objectId,
                adjacentVertices,
            });
        } else if (picker === Picker.HeightProfileEntity) {
            if (result.objectId === -1) {
                return;
            }

            dispatch(heightProfileActions.selectPoint({ id: result.objectId, pos: vec3.clone(position) }));
        } else if (picker === Picker.Pins) {
            // Does nothing since handling of clicking on the canvas is done on MarkerCreationMode.tsx for the pins picker. This case is added to avoid a confusing warning message.
        } else {
            console.warn("Picker not handled", picker);
        }
    };

    return handleCanvasPick;
}
